import React, { useEffect, useRef, useState } from 'react'
import { IconFolder, IconDotsVertical, IconTrash, IconEdit } from '@tabler/icons-react'
import { cn } from '../../enviroment/lib/utils'
import Loader from './Loader'

interface FolderButtonProps {
  label: string
  count: number
  isLoading?: boolean
  isSelected: boolean
  onClick: () => void
  onDeleteClick?: () => void
  onRenameSubmit?: (newName: string) => Promise<void>
}

const FolderButton: React.FC<FolderButtonProps> = ({
  label,
  count,
  isLoading = false,
  isSelected,
  onClick,
  onDeleteClick,
  onRenameSubmit,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isSavingRename, setIsSavingRename] = useState(false)
  const [renameValue, setRenameValue] = useState(label)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Prevents onBlur from cancelling when Enter already submitted
  const isSubmittingRef = useRef(false)

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isRenaming])

  const handleDotsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMenuOpen((prev) => !prev)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMenuOpen(false)
    onDeleteClick?.()
  }

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMenuOpen(false)
    setRenameValue(label)
    setIsRenaming(true)
  }

  const handleBlur = (e: React.FocusEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.relatedTarget as Node)) {
      setIsMenuOpen(false)
    }
  }

  const submitRename = async () => {
    const trimmed = renameValue.trim()
    try {
      if (trimmed && trimmed !== label) {
        setIsSavingRename(true)
        await onRenameSubmit?.(trimmed)
      }
    } finally {
      isSubmittingRef.current = false
      setIsSavingRename(false)
      setIsRenaming(false)
    }
  }

  const cancelRename = () => {
    if (isSubmittingRef.current || isSavingRename) return
    setRenameValue(label)
    setIsRenaming(false)
  }

  const handleRenameKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      isSubmittingRef.current = true
      await submitRename()
    } else if (e.key === 'Escape') {
      cancelRename()
    }
  }

  return (
    <div className="group relative">
      <div
        role="button"
        tabIndex={0}
        onClick={isRenaming ? undefined : onClick}
        onKeyDown={(e) => !isRenaming && e.key === 'Enter' && onClick()}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-lg px-4 py-2.5 text-left transition-colors',
          isSelected && !isRenaming
            ? 'bg-gray-700 text-gray-50'
            : 'text-gray-50 hover:bg-gray-700/60 hover:text-gray-50',
          isRenaming && 'cursor-default',
        )}
      >
        <IconFolder className="h-4 w-4 shrink-0" />
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={cancelRename}
            onClick={(e) => e.stopPropagation()}
            disabled={isSavingRename}
            maxLength={50}
            className={cn(
              'w-0 max-w-[110px] flex-1 truncate rounded-[4px] border border-gray-700 bg-gray-800 px-1 text-sm font-medium text-gray-50 outline-none selection:bg-blue-500',
              isSavingRename && 'opacity-60',
            )}
          />
        ) : (
          <span className="flex-1 truncate text-sm">{label}</span>
        )}
        {(isLoading || isSavingRename) && <Loader className="h-3.5 w-3.5 shrink-0 border-2 text-light-green-500" />}
        {!isRenaming && (
          <div className="flex items-center">
            <span className="text-xs text-gray-200">{count}</span>
            {onDeleteClick && (
              <div
                className={cn(
                  'w-0 overflow-hidden transition-all duration-200 group-hover:ml-1.5 group-hover:w-5',
                  isMenuOpen && 'ml-1.5 w-5',
                )}
              >
                <button
                  onClick={handleDotsClick}
                  onBlur={handleBlur}
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100',
                    'text-gray-400 hover:bg-gray-600 hover:text-gray-50',
                    isMenuOpen && 'opacity-100',
                  )}
                  aria-label="Folder options"
                >
                  <IconDotsVertical className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isMenuOpen && onDeleteClick && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full z-50 mt-1 overflow-hidden rounded-[4px] border border-gray-700 bg-gray-800 shadow-lg"
        >
          {onRenameSubmit && (
            <button
              onClick={handleRenameClick}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-50 transition-colors hover:bg-gray-700"
            >
              <IconEdit className="h-4 w-4" />
              Rename
            </button>
          )}
          <button
            onClick={handleDeleteClick}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-red-300 transition-colors hover:bg-red-300/20"
          >
            <IconTrash className="h-4 w-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

export default FolderButton
